const m = require('mithril');
const BasePlugin = require('./basePlugin');
const { getLocationCoordinates, processGeoJSON, getChildInfo, parentIconTemplate, isValidLocation, createMarker, isLocationClass } = require('../mapUtils');

const leafletMarkerCluster = require('leaflet.markercluster');
const leafletMarkerClusterDefaultStyle = require('leaflet.markercluster/dist/MarkerCluster.Default.css');
const leafletMarkerClusterStyle = require('leaflet.markercluster/dist/MarkerCluster.css');

class MarkerClustersPlugin extends BasePlugin {
  constructor(ctrl) {
    super(ctrl);

    
    const markerCache = {};
    const spiderfyOverlayState = { container: null, activeClusterId: null };

    const ensureSpiderfyOverlay = () => {
      if (spiderfyOverlayState.container) return spiderfyOverlayState.container;
      if (!ctrl?.map || !ctrl.map.getContainer) return null;
      const mapContainer = ctrl.map.getContainer();
      if (!mapContainer) return null;
      const overlay = document.createElement('div');
      overlay.className = 'marker-spiderfy-overlay';
      mapContainer.appendChild(overlay);
      spiderfyOverlayState.container = overlay;
      return overlay;
    };

    const clearSpiderfyOverlay = () => {
      if (spiderfyOverlayState.container) {
        spiderfyOverlayState.container.classList.remove('is-visible');
        spiderfyOverlayState.container.innerHTML = '';
      }
      spiderfyOverlayState.activeClusterId = null;
    };

    const buildChildOrderMap = () => {
      const order = new Map();
      try {
        const childEdges = typeof ctrl.childFeatures === 'function'
          ? ctrl.childFeatures()
          : [];
        [...childEdges]
          .sort((a, b) => {
            const ai = Number.isFinite(a?.index) ? a.index : Number.MAX_SAFE_INTEGER;
            const bi = Number.isFinite(b?.index) ? b.index : Number.MAX_SAFE_INTEGER;
            return ai - bi;
          })
          .forEach((edge, idx) => {
            const rid = edge?.in?.['@rid'] || edge?.['@rid'];
            if (rid) order.set(rid, idx);
          });
      } catch (e) {
        console.error('Error building child order map', e);
      }
      return order;
    };

    const renderSpiderfyOverlay = (markers, clusterId) => {
      const overlay = ensureSpiderfyOverlay();
      if (!overlay) return;

      overlay.innerHTML = '';
      markers.forEach(marker => {
        const item = document.createElement('div');
        item.className = 'marker-spiderfy-item';

        const link = document.createElement('a');
        link.className = 'map-divicon-content fw4';
        const href = marker?.options?.title || marker?.title;
        if (href) link.setAttribute('href', `#!${href}`);
        link.textContent = marker?.name || '—';

        item.appendChild(link);
        overlay.appendChild(item);
      });

      spiderfyOverlayState.activeClusterId = clusterId ?? null;
      overlay.classList.add('is-visible');
    };

    const toggleClusterOverlay = (cluster) => {
      if (!cluster) return;
      const clusterId = cluster._leaflet_id;
      if (spiderfyOverlayState.activeClusterId === clusterId) {
        clearSpiderfyOverlay();
        return;
      }
      const markers = typeof cluster.getAllChildMarkers === 'function'
        ? cluster.getAllChildMarkers()
        : [];
      const order = buildChildOrderMap();
      const sortedMarkers = [...markers].sort((a, b) => {
        const aRid = a?.UID ? ctrl.parseFeatureUid(a.UID)?.rid : a?.['@rid'];
        const bRid = b?.UID ? ctrl.parseFeatureUid(b.UID)?.rid : b?.['@rid'];
        const aIdx = aRid ? order.get(aRid) : undefined;
        const bIdx = bRid ? order.get(bRid) : undefined;
        if (Number.isFinite(aIdx) || Number.isFinite(bIdx)) {
          if (!Number.isFinite(aIdx)) return 1;
          if (!Number.isFinite(bIdx)) return -1;
          return aIdx - bIdx;
        }
        const ai = Number.isFinite(a?._sortIndex) ? a._sortIndex : Number.MAX_SAFE_INTEGER;
        const bi = Number.isFinite(b?._sortIndex) ? b._sortIndex : Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return (a?.name || '').localeCompare(b?.name || '');
      });
      renderSpiderfyOverlay(sortedMarkers, clusterId);
    };

    const handleFeatureAdditions = async (features) => {
      if (!Array.isArray(features)) return;
      features.forEach(feature => {
        let context = ctrl.contexts.find(c => c.name === feature.context);
        let locationRecord = feature.in ? feature.in : feature;

        if (!isLocationClass(locationRecord['@class'])) return;
        if (!isValidLocation(locationRecord)) return;

        const includeMarkers = ctrl.locationConfig()?.markers?.includes(context.name);
        if (!includeMarkers) return;

        const locationId = locationRecord['@rid'].toString().slice(1);
        const href = `/location/${locationId}`;
        const polygon = processGeoJSON(locationRecord, locationId);
        if (!polygon || polygon?.geojson?.features.length == 0) return;
        const coordinates = polygon && polygon.getBounds()._southWest
          ? polygon.getBounds().getCenter()
          : getLocationCoordinates(locationRecord);
        const childInfo = getChildInfo(locationRecord);
        const iconHtml = parentIconTemplate(
          locationRecord.name,
          href,
          childInfo.childClass,
          childInfo.childCount
        );
        
        const sortIndex = Number.isFinite(feature?.index)
          ? feature.index
          : Number.isFinite(locationRecord?.index)
            ? locationRecord.index
            : null;

        // Check if marker already exists
        let marker = markerCache[feature.UID];
        if (!marker) {
          // Create new marker and add to cache
          marker = createMarker(locationRecord, coordinates, iconHtml, href);
          marker.name = locationRecord.name;
          marker.type = 'Point';
          marker.UID = feature.UID;
          marker.context = context.name;
          marker._sortIndex = sortIndex;
          markerCache[feature.UID] = marker;

          // Add marker to layers
          try {
            ctrl.markerClusterGroup.addLayer(marker);
            ctrl.allLayers.push(marker);
          } catch (e) {
            console.error('Error adding marker:', marker, e);
          }
        } else {
          marker._sortIndex = sortIndex;
          console.log('Marker already exists in cache:', marker.name, marker.UID);
        }
      });
    };

    const handleFeatureRemovals = async (featureUids) => {
      featureUids.forEach(featureUid => {
        let marker = markerCache[featureUid];
        if (marker) {
          try {
            ctrl.markerClusterGroup.removeLayer(marker);
            let featureIndex = ctrl.allLayers.indexOf(marker);
            if (featureIndex > -1) ctrl.allLayers.splice(featureIndex, 1);
            delete markerCache[featureUid];
          } catch (e) {
            console.error('Error removing marker:', marker, e);
          }
        }
      });
      clearSpiderfyOverlay();
    };

    const createClusterGroup = async () => {
      // Remove existing marker cluster group if it exists
      if (ctrl.markerClusterGroup) {
        ctrl.map.removeLayer(ctrl.markerClusterGroup);
        ctrl.markerClusterGroup.clearLayers();
        ctrl.markerClusterGroup = null;
      }

      // Create new marker cluster group
      ctrl.markerClusterGroup = L.markerClusterGroup({
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        // custom icon function to display the number of child locations
        iconCreateFunction: (cluster) => {
          const childCount = cluster.getChildCount();
          return L.divIcon({
            className: 'map-divicon',
            html: markerGroupHtml(childCount, ctrl.childClass),
            iconAnchor: [0, 0],
          });
        },
        spiderfyOnMaxZoom: false
      });

      ctrl.markerClusterGroup.on('clusterclick', (e) => {
        if (ctrl.location()['@class'] === 'Building') {
          toggleClusterOverlay(e.layer);
        } else {
          clearSpiderfyOverlay();
          e.layer.zoomToBounds({padding: [30, 30]});
        }
      });

      ctrl.map.on('zoomstart movestart', clearSpiderfyOverlay);
      ctrl.map.on('click', clearSpiderfyOverlay);

      ctrl.map.addLayer(ctrl.markerClusterGroup);
    };

        // Register event listeners outside of mithril:oncreate
    ctrl.eventBus.on('featuresToAdd', handleFeatureAdditions);
    ctrl.eventBus.on('featuresToRemove', handleFeatureRemovals);

    ctrl.eventBus.on('mithril:oncreate', () => {
      createClusterGroup();
    });

    ctrl.eventBus.on('mithril:onremove', () => {
      clearSpiderfyOverlay();
      if (spiderfyOverlayState.container?.parentNode) {
        spiderfyOverlayState.container.parentNode.removeChild(spiderfyOverlayState.container);
      }
      spiderfyOverlayState.container = null;
    });
  }
}

function markerGroupHtml(count, childName) {
  return `
    <div class="map-divicon fw4">
      <a class="map-divicon-content">${count} ${childName}${count > 1 ? 's' : ''}</a>
    </div>
  `;
}

module.exports = MarkerClustersPlugin;
  
